import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { createPortal } from 'react-dom';

import AudioPointPopupMenu from '~/editor/audioPointPopupMenu/AudioPointPopupMenu';
import { useEditorStore } from '~/shell/Store';
import { select } from '~/lib/select';
import { shallow } from 'zustand/shallow';
import {
  SequenceStore,
  useSequenceStore
} from '~/training/sequence/SequenceStore';
import { CloseButton } from '~/ui/buttons/CloseButton';
import TextAudioPointIcon from '~/assets/icons/text-audio-point.svg';
import MicrophoneAudioPointIcon from '~/assets/icons/audio-point-microphone.svg';

const AudioIconButton = styled.button<{ $isEditor: boolean }>`
  position: absolute;
  width: ${props => (props.$isEditor ? '48px' : '70px')};
  height: ${props => (props.$isEditor ? '48px' : '70px')};
  border: none;
  background: ${props =>
    props.$isEditor ? 'transparent' : 'rgba(0, 0, 0, 0.5)'};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: all 0.2s ease;
  transform: translate(-50%, -50%);
  padding: 0;
  border-radius: ${props => (props.$isEditor ? '0' : '50%')};

  &::before {
    content: '';
    position: absolute;
    top: -8px;
    left: -8px;
    right: -8px;
    bottom: -8px;
    border-radius: 8px;
    border: 2px solid transparent;
    background: transparent;
    transition: all 0.2s ease;
    z-index: -1;
    display: ${props => (props.$isEditor ? 'block' : 'none')};
    width: 55px;
    height: 55px;
  }

  &:hover {
    transform: translate(-50%, -50%) scale(1.1);
  }

  &:hover::before {
    background: rgba(151, 56, 255, 0.1);
    border-color: #9738ff;
  }

  &:active {
    transform: translate(-50%, -50%) scale(0.95);
  }

  svg {
    width: 48px;
    height: 48px;
  }

  .number {
    color: black;
    font-family: Raleway;
    font-weight: 700;
    font-size: 20px;
    line-height: 1;
    transition: color 0.2s ease;
  }

  .preview-circle {
    width: 50px;
    height: 50px;
    background: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      width: 24px;
      height: 18px;
    }
  }
`;

const PopupContent = styled.div`
  padding: 16px;
  min-width: 300px;
  max-width: 450px;

  .title {
    font-family: Raleway;
    font-weight: 700;
    font-size: 16px;
    color: #333;
    margin-bottom: 8px;
  }

  .text {
    font-family: Raleway;
    font-size: 14px;
    color: #666;
    margin-bottom: 16px;
    line-height: 1.4;
  }

  .audio-player {
    margin-bottom: 8px;
  }

  .preview-modal-content {
    min-width: 400px;
    padding: 24px;
  }

  .play-section {
    display: flex;
    align-items: center;
    gap: 20px;
  }

  .play-button {
    width: 62px;
    height: 62px;
    border: none;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    padding: 0;
    transition: all 0.2s ease;

    &:hover {
      transform: scale(1.05);
    }

    &:active {
      transform: scale(0.95);
    }
  }

  .text-content {
    flex: 1;
  }

  .text-content p {
    font-family: Raleway;
    font-weight: 400;
    font-size: 18px;
    color: black;
    margin: 0;
    line-height: 1.4;
  }

  .progress-bar {
    width: 100%;
    height: 8px;
    background: #e8e8e8;
    border-radius: 4px;
    overflow: hidden;
    margin-top: 8px;
  }

  .progress-fill {
    height: 100%;
    background: #9738ff;
    border-radius: 4px;
    transition: width 0.1s ease;
  }
`;

const CustomModal = styled.div`
  position: fixed;
  background: white;
  border-radius: 12px;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  max-width: 90vw;
  max-height: 90vh;
  overflow: hidden;
`;

// Modal wrapper with closeBtn positioning - same as AddTextAudioModal
const ModalWrapper = styled.div`
  .closeBtn {
    margin-left: calc(100% - 70px);
  }
`;

interface AudioPointData {
  id: string;
  text: string;
  body?: string;
  audio: {
    id: number;
    mediaUrl: string;
    originalFilename?: string;
  } | null;
  coordinates: {
    x: number;
    y: number;
  };
  minX?: number;
  maxX?: number;
  minY?: number;
  maxY?: number;
}

interface AudioPointProps {
  data: AudioPointData;
  onClick?: () => void;
  number?: number;
  onEdit?: (data: AudioPointData) => void;
  onMove?: (id: string, newCoordinates: { x: number; y: number }) => void;
  onDelete?: (id: string) => void;
}

const AudioPoint: React.FC<AudioPointProps> = ({
  data,
  onClick,
  number,
  onEdit,
  onMove,
  onDelete
}) => {
  const { editorMode } = useEditorStore();
  const seqSelector = (s: SequenceStore) =>
    select(s, 'setAudioPlayed', 'addBlocker', 'removeBlocker');
  const { addBlocker, removeBlocker, setAudioPlayed } = useSequenceStore(
    seqSelector,
    shallow
  );
  const [showPopup, setShowPopup] = useState(false);
  // Removed isPlaying state - no auto play on click
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [hasPlayedAudio, setHasPlayedAudio] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [audioProgress, setAudioProgress] = useState(0);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    // En modo editor: mostrar menú contextual
    if (editorMode) {
      // Cerrar cualquier otro menú contextual abierto
      document.dispatchEvent(new CustomEvent('closeAllAudioPointMenus'));

      // Context menu opened
      setShowContextMenu(true);
      return;
    }

    // En modo normal: abrir popup con información
    setShowPopup(true);

    // Calcular posición del popup - arriba del botón
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const popupWidth = 450; // Ancho estimado del popup
      const popupHeight = 200; // Alto estimado del popup

      let left = rect.left + rect.width / 2 - popupWidth / 2;
      let top = rect.top - popupHeight - 10; // Posicionar arriba del botón

      // Ajustar si se sale de la pantalla horizontalmente
      if (left < 10) left = 10;
      if (left + popupWidth > window.innerWidth - 10) {
        left = window.innerWidth - popupWidth - 10;
      }

      // Si no hay espacio arriba, ponerlo abajo
      if (top < 10) {
        top = rect.bottom + 10;
      }

      setPopupPosition({ top, left });
    }

    if (onClick) {
      onClick();
    }
  };

  const handlePlayAudio = () => {
    if (!data.audio?.mediaUrl) return;

    // Stop any currently playing audio
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }

    const audio = new Audio(data.audio.mediaUrl);
    currentAudioRef.current = audio;

    audio.addEventListener('timeupdate', () => {
      if (audio.duration) {
        const progress = (audio.currentTime / audio.duration) * 100;
        setAudioProgress(progress);
      }
    });

    audio.addEventListener('play', () => {
      setIsAudioPlaying(true);
      setHasPlayedAudio(true);
    });

    audio.addEventListener('pause', () => {
      setIsAudioPlaying(false);
    });

    audio.addEventListener('ended', () => {
      setIsAudioPlaying(false);
      setAudioProgress(100);
      // Mirror AudioPopup: mark played and remove blocker when audio fully completes (non-editor mode)
      if (!editorMode) {
        setAudioPlayed(data.id);
        removeBlocker(data.id);
      }
    });

    audio.play().catch(() => {
      // Silently ignore play interruption errors
    });
  };

  const handleToggleAudio = () => {
    if (currentAudioRef.current) {
      if (isAudioPlaying) {
        currentAudioRef.current.pause();
      } else {
        currentAudioRef.current.play().catch(() => {
          // Silently ignore play interruption errors
        });
      }
    } else {
      handlePlayAudio();
    }
  };

  // Clean up audio when component unmounts or popup closes
  useEffect(() => {
    if (!showPopup && currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
      setIsAudioPlaying(false);
      setAudioProgress(0);
    }
  }, [showPopup]);

  // Blockers: behave like AudioPopup in preview/learner mode
  useEffect(() => {
    if (editorMode) return; // no gating in editor mode
    // Add a blocker for this point on mount
    addBlocker(data.id);
    return () => {
      removeBlocker(data.id);
    };
  }, []);

  // For text-only points (no audio), remove blocker on first open
  useEffect(() => {
    if (editorMode) return;
    const hasMedia = !!data.audio?.mediaUrl;
    if (!hasMedia && showPopup) {
      removeBlocker(data.id);
    }
  }, [showPopup, editorMode, data.id, data.audio?.mediaUrl, removeBlocker]);

  // Removed handleContextMenu - now using left-click in editor mode

  // Listen for global event to close all menus
  useEffect(() => {
    const handleCloseAllMenus = () => {
      setShowContextMenu(false);
    };

    document.addEventListener('closeAllAudioPointMenus', handleCloseAllMenus);

    return () => {
      document.removeEventListener(
        'closeAllAudioPointMenus',
        handleCloseAllMenus
      );
    };
  }, []);

  const handleEditAudioPoint = () => {
    if (onEdit) {
      onEdit(data);
    }
  };

  const handleDeleteAudioPoint = () => {
    if (onDelete) {
      onDelete(data.id);
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    // Only allow dragging in editor mode
    if (!editorMode) return;

    // Don't start drag on right-click (for context menu)
    if (e.button === 2) return;

    e.preventDefault();
    e.stopPropagation();

    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    setDragOffset({ x: 0, y: 0 });
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging || !editorMode) return;

    // Get image container dimensions for accurate percentage calculation
    const imageContainer = document.querySelector(
      '[data-image-container="true"]'
    );
    if (imageContainer) {
      const newOffset = {
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      };

      setDragOffset(newOffset);
    }
  };

  const handleMouseUp = () => {
    if (!isDragging || !editorMode) return;

    setIsDragging(false);

    // Calculate new position as percentage
    const imageContainer = document.querySelector(
      '[data-image-container="true"]'
    );

    if (
      imageContainer &&
      onMove &&
      (Math.abs(dragOffset.x) > 5 || Math.abs(dragOffset.y) > 5)
    ) {
      const rect = imageContainer.getBoundingClientRect();

      // Current position in pixels
      const currentX = (data.coordinates.x * rect.width) / 100;
      const currentY = (data.coordinates.y * rect.height) / 100;

      // New position in pixels
      let newX = currentX + dragOffset.x;
      let newY = currentY + dragOffset.y;

      if (data.minX && newX < data.minX) {
        newX = data.minX;
      }

      if (data.maxX && newX > data.maxX) {
        newX = data.maxX;
      }

      if (data.minY && newY < data.minY) {
        newY = data.minY;
      }
      if (data.maxY && newY > data.maxY) {
        newY = data.maxY;
      }

      // Convert to percentages and clamp to bounds
      const newPercentX = Math.max(0, Math.min(100, (newX * 100) / rect.width));
      const newPercentY = Math.max(
        0,
        Math.min(100, (newY * 100) / rect.height)
      );

      onMove(data.id, {
        x: parseInt(newPercentX.toFixed(2)),
        y: parseInt(newPercentY.toFixed(2))
      });
    }

    setDragOffset({ x: 0, y: 0 });
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node)
      ) {
        setShowPopup(false);
      }
    };

    if (showPopup) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showPopup]);

  // Add drag and drop event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart, dragOffset]);

  // Clean up audio on component unmount
  useEffect(() => {
    return () => {
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current = null;
      }
    };
  }, []);

  return (
    <>
      <AudioIconButton
        ref={buttonRef}
        $isEditor={editorMode}
        style={{
          left: isDragging
            ? `calc(${data.coordinates.x}% + ${dragOffset.x}px)`
            : `${data.coordinates.x}%`,
          top: isDragging
            ? `calc(${data.coordinates.y}% + ${dragOffset.y}px)`
            : `${data.coordinates.y}%`,
          cursor: editorMode ? (isDragging ? 'grabbing' : 'grab') : 'pointer',
          transform: isDragging ? 'scale(1.1)' : 'scale(1)',
          transition: isDragging ? 'none' : 'transform 0.2s ease',
          zIndex: isDragging ? 1000 : 'auto'
        }}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        title={data.text || 'Audio point'}
      >
        {editorMode ? (
          // Editor mode: Show text icon when there's NO audio, microphone when there's audio
          !data.audio?.mediaUrl ? (
            // Text only icon (no audio)
            <TextAudioPointIcon />
          ) : (
            // Audio point (with audio) - microphone icon
            <MicrophoneAudioPointIcon />
          )
        ) : (
          // Preview mode: Black circle (50% opacity) with white circle inside
          <div className="preview-circle">
            {hasPlayedAudio ? (
              // Show check mark if audio has been played
              <svg
                width="24"
                height="18"
                viewBox="0 0 24 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3 7L10 14L21 3"
                  stroke="black"
                  strokeWidth="5"
                  strokeLinecap="round"
                />
              </svg>
            ) : !data.audio?.mediaUrl ? (
              // Text-only: "+" icon — explicit color matches .number (black on white circle)
              <span style={{ fontSize: '40px', lineHeight: 1, color: 'black' }}>
                +
              </span>
            ) : (
              // Show number if audio hasn't been played yet
              <span className="number">{number || 1}</span>
            )}
          </div>
        )}
      </AudioIconButton>

      {/* Removed hidden audio element - no auto play functionality */}

      {/* Popup con información */}
      {showPopup &&
        createPortal(
          <CustomModal
            ref={popupRef}
            style={{
              top: `${popupPosition.top}px`,
              left: `${popupPosition.left}px`
            }}
            onClick={e => e.stopPropagation()}
          >
            <ModalWrapper>
              <CloseButton
                type="close"
                onClick={() => setShowPopup(false)}
                autoFocus={false}
              />
              <PopupContent>
                {editorMode ? (
                  // Editor mode: Show full audio player and controls
                  <>
                    {(data.text || data.body) && (
                      <>
                        <div className="title">Audio Point</div>
                        {data.text && <div className="text">{data.text}</div>}
                        {data.body && (
                          <div
                            className="text"
                            style={{ marginTop: data.text ? '4px' : '0' }}
                          >
                            {data.body}
                          </div>
                        )}
                      </>
                    )}

                    {data.audio?.mediaUrl && (
                      <div className="audio-player">
                        <audio
                          controls
                          preload="none"
                          style={{ width: '100%' }}
                          onPlay={() => setHasPlayedAudio(true)}
                        >
                          {data.audio.mediaUrl && (
                            <source src={data.audio.mediaUrl} />
                          )}
                          Your browser does not support the audio element.
                        </audio>
                      </div>
                    )}

                    {data.audio?.originalFilename && (
                      <div style={{ fontSize: '12px', color: '#999' }}>
                        {data.audio.originalFilename}
                      </div>
                    )}
                  </>
                ) : (
                  // Preview mode: Show audio player with play button design like in image
                  <>
                    {(data.text || data.body) && (
                      <div className="preview-modal-content">
                        <div className="play-section">
                          {/* Only show play button if audio exists */}
                          {data.audio?.mediaUrl && (
                            <button
                              className="play-button"
                              onClick={handleToggleAudio}
                            >
                              {isAudioPlaying ? (
                                // Pause icon - same size as play icon
                                <svg
                                  width="62"
                                  height="62"
                                  viewBox="0 0 62 62"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    opacity="0.1"
                                    d="M0.000212545 30.9105C-0.0554903 13.6954 14.4077 -0.446404 31.855 0.0107827C47.9748 0.443945 62.0673 13.7462 61.9998 31.1404C61.9586 42.2064 56.013 52.4113 46.4007 57.9068C36.7883 63.4048 24.9724 63.3613 15.4009 57.7931C5.82941 52.2249 -0.0407072 41.9772 0.000212545 30.9105Z"
                                    fill="#9738FF"
                                  />
                                  <path
                                    d="M8.00016 30.9102C7.95887 18.1507 18.6786 7.66914 31.6101 8.00799C43.5578 8.32904 54.0028 18.1884 53.9528 31.0805C53.9222 39.2824 49.5155 46.846 42.3911 50.9192C35.2666 54.9942 26.5089 54.9619 19.4148 50.8349C12.3206 46.7079 7.96983 39.1125 8.00016 30.9102Z"
                                    fill="#9738FF"
                                  />
                                  <path
                                    d="M23 21V41H29V21H23ZM33 21V41H39V21H33Z"
                                    fill="white"
                                  />
                                </svg>
                              ) : (
                                // Play icon
                                <svg
                                  width="62"
                                  height="62"
                                  viewBox="0 0 62 62"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    opacity="0.1"
                                    d="M0.000212545 30.9105C-0.0554903 13.6954 14.4077 -0.446404 31.855 0.0107827C47.9748 0.443945 62.0673 13.7462 61.9998 31.1404C61.9586 42.2064 56.013 52.4113 46.4007 57.9068C36.7883 63.4048 24.9724 63.3613 15.4009 57.7931C5.82941 52.2249 -0.0407072 41.9772 0.000212545 30.9105Z"
                                    fill="#9738FF"
                                  />
                                  <path
                                    d="M8.00016 30.9102C7.95887 18.1507 18.6786 7.66914 31.6101 8.00799C43.5578 8.32904 54.0028 18.1884 53.9528 31.0805C53.9222 39.2824 49.5155 46.846 42.3911 50.9192C35.2666 54.9942 26.5089 54.9619 19.4148 50.8349C12.3206 46.7079 7.96983 39.1125 8.00016 30.9102Z"
                                    fill="#9738FF"
                                  />
                                  <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M28.3333 37.4045L36.9658 30.5L28.3333 23.5955L28.3333 37.4045ZM27.8955 20.2578L39.5621 29.5892C40.146 30.0561 40.146 30.9439 39.5621 31.4108L27.8955 40.7422C27.1316 41.3532 26 40.8094 26 39.8314L26 21.1686C26 20.1906 27.1316 19.6468 27.8955 20.2578Z"
                                    fill="white"
                                  />
                                </svg>
                              )}
                            </button>
                          )}
                          <div className="text-content">
                            {data.text && (
                              <p
                                dangerouslySetInnerHTML={{ __html: data.text }}
                              ></p>
                            )}
                            {data.body && (
                              <p
                                style={{
                                  marginTop: data.text ? '8px' : '0',
                                  fontSize: '16px'
                                }}
                                dangerouslySetInnerHTML={{ __html: data.body }}
                              ></p>
                            )}
                          </div>
                        </div>
                        {/* Only show progress bar if audio exists */}
                        {data.audio?.mediaUrl && (
                          <div className="progress-bar">
                            <div
                              className="progress-fill"
                              style={{ width: `${audioProgress}%` }}
                            ></div>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </PopupContent>
            </ModalWrapper>
          </CustomModal>,
          document.body
        )}

      {/* Context Menu */}
      <AudioPointPopupMenu
        isOpen={showContextMenu}
        anchorElement={buttonRef.current}
        onClose={() => setShowContextMenu(false)}
        audioPointId={data.id}
        onEdit={handleEditAudioPoint}
        onDelete={handleDeleteAudioPoint}
      />
    </>
  );
};

export default AudioPoint;
